import { useCallback, useEffect, useRef } from 'react';
import type { CanvasNode, NodeData } from '../types';
import { forget, heldResultsFor, holdNodeResult } from '../utils/heldNodeResults';

type Guard = (current: NodeData) => boolean;
type ApplyPatch = (id: string, patch: Partial<NodeData>, guard?: Guard) => boolean;

interface HeldResultsOptions {
  /** The workspace that is open now. */
  workspaceKey: string | null;
  nodes: CanvasNode[];
  /** False while a workspace is loading: its nodes are not all there yet. */
  ready: boolean;
  /** Merges a patch into a node of the open workspace. */
  applyPatch: ApplyPatch;
  /**
   * True from the moment the open workspace is being left (its save has taken the canvas as it
   * was) until the canvas is replaced. A result that arrives then would go onto a canvas that is
   * about to be discarded, after its save: it is held instead, like one for a workspace that is
   * not open, and put on its node when the workspace is opened again.
   */
  leaving?: { readonly current: boolean };
}

/**
 * Keeps generation results that finish after the user switched workspace. Returns a patcher
 * for each node, tagged with the workspace the node was drawn in: a result for the open
 * workspace goes straight onto its node; one for another workspace is held, and put on its
 * node once that workspace is open again (and saved with it like any edit). So is a result
 * that arrives while its workspace is being left.
 */
export const useHeldNodeResults = ({ workspaceKey, nodes, ready, applyPatch, leaving }: HeldResultsOptions) => {
  const keyRef = useRef(workspaceKey);
  keyRef.current = workspaceKey;
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  const patcherFor = useCallback((originKey: string | null): ApplyPatch => (id, patch, guard) => {
    if (originKey && (originKey !== keyRef.current || leaving?.current)) {
      void holdNodeResult(originKey, id, patch, guard);
      return true;
    }
    return applyPatch(id, patch, guard);
  }, [applyPatch, leaving]);

  useEffect(() => {
    // (nothing held is put back on a canvas that is being left)
    if (!workspaceKey || !ready || leaving?.current) return;
    let alive = true;
    void heldResultsFor(workspaceKey).then((results) => {
      if (!alive || keyRef.current !== workspaceKey || leaving?.current) return;
      for (const result of results) {
        const node = nodesRef.current.find(n => n.id === result.nodeId);
        if (!node) continue; // not loaded yet; a deleted node's result is let go after a while
        if (result.guard && !result.guard(node.data)) { void forget(workspaceKey, result.nodeId); continue; }
        if (applyPatch(result.nodeId, result.patch)) void forget(workspaceKey, result.nodeId);
      }
    });
    return () => { alive = false; };
  }, [workspaceKey, nodes, ready, applyPatch, leaving]);

  return patcherFor;
};
